import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Select,
  Space,
  Spin,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
  notification,
} from 'antd';
import { AppstoreOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import {
  ACCESS_CATALOG_STATES,
  ACCESS_MODULES,
  APPLICATIONS,
  applicationLabel,
} from '../../assets/utils/accessCatalog';
import { useAuth } from '../../assets/utils/useAuth';
import {
  createAccessModule,
  deleteAccessModule,
  fetchAccessModules,
  fetchPermissions,
  updateAccessModule,
} from '../dash/rbacApi';
import { ADMIN_REVIEW_MODAL_CLASS } from '../dashboard/utils';
import DashboardSection from '../dashboard/components/DashboardSection';

const { Text, Paragraph } = Typography;

const normalizeList = (payload) => {
  if (!payload) return [];
  const container = payload?.data ?? payload;
  if (Array.isArray(container)) return container;
  return container?.data ?? container?.items ?? container?.results ?? [];
};

const BUILT_IN_MODULE_KEYS = new Set(ACCESS_MODULES.map((mod) => mod.key));

const moduleKeyOf = (record) => record?.key || record?.module_key || '';
const moduleApplicationOf = (record) => record?.application || 'care-admin';
const isRowActive = (record) => record?.is_active !== false;
const isRowNavVisible = (record) => record?.nav_visible !== false;

/**
 * Why a row does or does not reach Care Admin.
 *
 * The app fetches the catalog with `application=care-admin` and the API drops inactive rows,
 * so anything failing those two tests never arrives — Care Admin then quietly uses its
 * built-in mapping for that key and edits made here have no effect.
 */
const rowReach = (record) => {
  const application = moduleApplicationOf(record);
  const blockers = [];
  if (application !== 'care-admin') {
    blockers.push({
      tag: `Application: ${applicationLabel(application)}`,
      reason: `Care Admin only loads rows whose application is "care-admin". This row is registered to "${application}", so Care Admin never receives it and falls back to its built-in mapping for this module.`,
    });
  }
  if (!isRowActive(record)) {
    blockers.push({
      tag: 'Inactive',
      reason: 'Inactive rows are excluded from the catalog the app loads, so Care Admin falls back to its built-in mapping for this module.',
    });
  }
  return blockers;
};

export default function AccessModulesTab({ onCatalogChange }) {
  const { accessCatalogState } = useAuth();
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [permissionNames, setPermissionNames] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  const loadModules = useCallback(async () => {
    setLoading(true);
    try {
      // No `application` filter on purpose: a row whose application was changed away from
      // care-admin is exactly the row an operator needs to see here, and filtering would
      // hide the misconfiguration that makes their edits do nothing.
      const response = await fetchAccessModules({
        include_inactive: 1,
      });
      setModules(normalizeList(response));
    } catch (error) {
      notification.error({
        message: 'Unable to load modules',
        description: error?.response?.data?.message || error?.message,
        placement: 'topLeft',
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadModules();
    fetchPermissions({ limit: 1000 })
      .then((response) => {
        const rows = normalizeList(response);
        setPermissionNames(
          rows.map((p) => p?.name).filter(Boolean).sort()
        );
      })
      .catch(() => {});
  }, [loadModules]);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({
      application: 'care-admin',
      module_group: 'Other',
      any_of: [],
      routes: [],
      sort_order: 100,
      allow_home: false,
      nav_visible: true,
      is_active: true,
    });
    setModalOpen(true);
  };

  const openEdit = (record) => {
    setEditing(record);
    form.setFieldsValue({
      module_key: record.key || record.module_key,
      label: record.label,
      module_group: record.group || record.module_group || 'Other',
      application: record.application || 'care-admin',
      description: record.description,
      any_of: record.anyOf || record.any_of || [],
      routes: record.routes || [],
      sort_order: record.sort_order || 0,
      allow_home: Boolean(record.allow_home),
      nav_visible: record.nav_visible !== false,
      is_active: record.is_active !== false,
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      const payload = {
        module_key: values.module_key,
        label: values.label,
        module_group: values.module_group,
        application: values.application,
        description: values.description || '',
        any_of: values.any_of || [],
        routes: values.routes || [],
        sort_order: Number(values.sort_order || 0),
        allow_home: Boolean(values.allow_home),
        nav_visible: Boolean(values.nav_visible),
        is_active: Boolean(values.is_active),
      };
      const response = editing?.id
        ? await updateAccessModule(editing.id, payload)
        : await createAccessModule(payload);
      if (response?.error) {
        throw new Error(response.message || 'Save failed');
      }
      notification.success({
        message: editing ? 'Module updated' : 'Module registered',
        placement: 'topLeft',
      });
      setModalOpen(false);
      setEditing(null);
      form.resetFields();
      await loadModules();
      await onCatalogChange?.();
    } catch (error) {
      if (error?.errorFields) return;
      notification.error({
        message: 'Unable to save module',
        description: error?.response?.data?.message || error?.message,
        placement: 'topLeft',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (record) => {
    try {
      const response = await deleteAccessModule(record.id);
      if (response?.error) {
        throw new Error(response.message || 'Delete failed');
      }
      notification.success({ message: 'Module deleted', placement: 'topLeft' });
      await loadModules();
      await onCatalogChange?.();
    } catch (error) {
      notification.error({
        message: 'Unable to delete module',
        description: error?.response?.data?.message || error?.message,
        placement: 'topLeft',
      });
    }
  };

  const permissionOptions = useMemo(
    () => permissionNames.map((name) => ({ value: name, label: name })),
    [permissionNames]
  );

  const diagnostics = useMemo(() => {
    const registeredKeys = new Set(modules.map(moduleKeyOf).filter(Boolean));
    const unreachable = modules.filter((record) => rowReach(record).length > 0);
    const hiddenFromNav = modules.filter(
      (record) => rowReach(record).length === 0 && !isRowNavVisible(record)
    );
    const unregisteredBuiltIns = ACCESS_MODULES
      .filter((mod) => !registeredKeys.has(mod.key))
      .map((mod) => mod.key);
    return { unreachable, hiddenFromNav, unregisteredBuiltIns };
  }, [modules]);

  const columns = [
    {
      title: 'Module',
      dataIndex: 'label',
      key: 'label',
      // Widths are set on every column because without them antd shares the
      // space by content, and this column loses: module keys are single
      // unbreakable tokens (notificationsCenter, preauthAnalytics) that get
      // broken mid-word rather than given room.
      width: 230,
      render: (text, record) => (
        <Space direction="vertical" size={0}>
          <Text strong style={{ wordBreak: 'normal', overflowWrap: 'anywhere' }}>{text}</Text>
          <Text type="secondary" style={{ fontSize: 12, wordBreak: 'normal', overflowWrap: 'anywhere' }}>
            {record.key || record.module_key}
          </Text>
        </Space>
      ),
    },
    {
      title: 'Group',
      dataIndex: 'group',
      key: 'group',
      width: 110,
      render: (value, record) => <Tag>{value || record.module_group || 'Other'}</Tag>,
    },
    {
      title: 'Routes',
      dataIndex: 'routes',
      key: 'routes',
      width: 260,
      render: (routes) => (
        <Space wrap size={[4, 4]}>
          {(routes || []).map((route) => (
            <Tag key={route} color="geekblue">{route}</Tag>
          ))}
        </Space>
      ),
    },
    {
      title: 'Any of',
      dataIndex: 'anyOf',
      key: 'anyOf',
      width: 250,
      render: (anyOf, record) => (
        <Space wrap size={[4, 4]}>
          {(anyOf || record.any_of || []).map((perm) => (
            <Tag key={perm}>{perm}</Tag>
          ))}
        </Space>
      ),
    },
    {
      title: 'Reaches Care Admin',
      key: 'reach',
      width: 240,
      render: (_, record) => {
        const blockers = rowReach(record);
        if (blockers.length) {
          return (
            <Space direction="vertical" size={2}>
              <Space wrap size={[4, 4]}>
                {blockers.map((blocker) => (
                  <Tooltip key={blocker.tag} title={blocker.reason}>
                    <Tag color="red">{blocker.tag}</Tag>
                  </Tooltip>
                ))}
              </Space>
              <Text type="danger" style={{ fontSize: 12 }}>
                {BUILT_IN_MODULE_KEYS.has(moduleKeyOf(record))
                  ? 'Edits here have no effect — Care Admin falls back to its built-in mapping for this module.'
                  : 'Edits here have no effect — Care Admin never receives this row, so the module is not gated or shown at all.'}
              </Text>
            </Space>
          );
        }
        if (!isRowNavVisible(record)) {
          return (
            <Space direction="vertical" size={2}>
              <Tooltip title="This row reaches the app and gates its routes, but it is deliberately kept out of the sidebar. Turn on “Show in sidebar” if it should appear in navigation.">
                <Tag color="orange">Not in sidebar</Tag>
              </Tooltip>
              <Text type="secondary" style={{ fontSize: 12 }}>Routes only</Text>
            </Space>
          );
        }
        return <Tag color="green">In effect</Tag>;
      },
    },
    {
      title: 'Flags',
      key: 'flags',
      width: 140,
      render: (_, record) => (
        <Space wrap size={[4, 4]}>
          {record.is_system ? <Tag color="red">Built-in</Tag> : <Tag color="green">Custom</Tag>}
          {record.nav_visible !== false ? <Tag color="blue">Nav</Tag> : null}
          {record.allow_home ? <Tag color="purple">Landing</Tag> : null}
          {record.is_active !== false ? (
            <Badge status="success" text="Active" />
          ) : (
            <Badge status="default" text="Inactive" />
          )}
        </Space>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 110,
      render: (_, record) => (
        <Space size="small">
          <Button type="link" onClick={() => openEdit(record)}>Edit</Button>
          {record.is_system ? null : (
            <Popconfirm
              title="Delete module"
              description="This removes the registry entry. It does not delete a React page."
              onConfirm={() => handleDelete(record)}
              okType="danger"
            >
              <Button type="link" danger>Delete</Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <>
      <DashboardSection
        id="rbac-modules"
        icon={<AppstoreOutlined />}
        title="Access modules"
        subtitle="Map permissions to routes, landing pages, and sidebar items"
        collapsible={false}
        variant="content"
        action={(
          <Space>
            <Button size="small" icon={<ReloadOutlined />} onClick={loadModules} loading={loading}>
              Refresh
            </Button>
            <Button size="small" type="primary" icon={<PlusOutlined />} onClick={openCreate}>
              Register module
            </Button>
          </Space>
        )}
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="This registers access, not a new screen"
          description="Routes you enter must already exist in Care Admin. Extra sidebar links can point at an existing path. A new URL without a React page will 404."
        />
        {accessCatalogState !== ACCESS_CATALOG_STATES.LIVE ? (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            message={
              accessCatalogState === ACCESS_CATALOG_STATES.EMPTY
                ? 'Your session loaded an empty module catalog'
                : 'Your session could not load the module catalog'
            }
            description={
              accessCatalogState === ACCESS_CATALOG_STATES.EMPTY
                ? 'The catalog request succeeded but returned no rows for care-admin, so this session is running entirely on Care Admin’s built-in permission mapping. Nothing registered here is in play until rows load.'
                : 'The catalog request has not succeeded in this session, so Care Admin is running on its built-in permission mapping. Changes made here will not be reflected until the catalog loads (sign out and back in, or reload).'
            }
          />
        ) : null}
        {diagnostics.unreachable.length ? (
          <Alert
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
            message={`${diagnostics.unreachable.length} module row${diagnostics.unreachable.length === 1 ? '' : 's'} cannot reach Care Admin`}
            description={(
              <Space direction="vertical" size={4}>
                <Text>
                  Care Admin loads only rows with application <Text code>care-admin</Text> that are
                  Active. These rows are excluded, so the app silently uses its built-in mapping for
                  those modules and edits made here do nothing:
                </Text>
                <Space wrap size={[4, 4]}>
                  {diagnostics.unreachable.map((record) => (
                    <Tag color="red" key={record.id || moduleKeyOf(record)}>
                      {moduleKeyOf(record) || record.label}
                    </Tag>
                  ))}
                </Space>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Fix by editing the row: set Application to Care Admin and turn Active on.
                </Text>
              </Space>
            )}
          />
        ) : null}
        {diagnostics.unregisteredBuiltIns.length ? (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message={`${diagnostics.unregisteredBuiltIns.length} built-in module${diagnostics.unregisteredBuiltIns.length === 1 ? ' has' : 's have'} no row here`}
            description={(
              <Space direction="vertical" size={4}>
                <Text>
                  These modules ship with Care Admin and are gated by the mapping compiled into the
                  app. They have no registry row, so there is nothing here to edit — register a row
                  with the same key to take control of one.
                </Text>
                <Space wrap size={[4, 4]}>
                  {diagnostics.unregisteredBuiltIns.map((key) => (
                    <Tag key={key}>{key}</Tag>
                  ))}
                </Space>
              </Space>
            )}
          />
        ) : null}
        {diagnostics.hiddenFromNav.length ? (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message={`${diagnostics.hiddenFromNav.length} module row${diagnostics.hiddenFromNav.length === 1 ? '' : 's'} will not appear in the sidebar`}
            description={(
              <Space direction="vertical" size={4}>
                <Text>
                  These rows do reach the app and gate their routes, but “Show in sidebar” is off so
                  no navigation item is added for them.
                </Text>
                <Space wrap size={[4, 4]}>
                  {diagnostics.hiddenFromNav.map((record) => (
                    <Tag color="orange" key={record.id || moduleKeyOf(record)}>
                      {moduleKeyOf(record) || record.label}
                    </Tag>
                  ))}
                </Space>
              </Space>
            )}
          />
        ) : null}
        {loading && !modules.length ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
            <Spin />
          </div>
        ) : (
          /* scroll.x gives the table `width: 1340px; min-width: 100%` (rc-table
             Table.js:298), so it stretches to fill a wider container and scrolls
             instead of squeezing the columns when the screen is narrower. */
          <Table
            rowKey={(record) => record.id || record.key}
            loading={loading}
            columns={columns}
            dataSource={modules}
            size="small"
            pagination={false}
            scroll={{ x: 1340 }}
          />
        )}
      </DashboardSection>

      <Modal
        title={editing ? 'Edit module' : 'Register module'}
        open={modalOpen}
        confirmLoading={saving}
        rootClassName={ADMIN_REVIEW_MODAL_CLASS}
        className={`${ADMIN_REVIEW_MODAL_CLASS}__dialog`}
        onCancel={() => {
          setModalOpen(false);
          setEditing(null);
          form.resetFields();
        }}
        okText={editing ? 'Update module' : 'Register module'}
        onOk={handleSave}
      >
        <Form layout="vertical" form={form}>
          <Paragraph type="secondary">
            Key is stable (used by roles as a landing page). Routes are Care Admin paths such as <Text code>/claims</Text>.
          </Paragraph>
          <Form.Item
            label="Key"
            name="module_key"
            rules={[
              { required: true, message: 'Key is required' },
              { pattern: /^[a-z][a-zA-Z0-9_]*$/, message: 'Start with a letter; letters, numbers, underscores' },
            ]}
          >
            <Input placeholder="e.g. facilityTypes" disabled={Boolean(editing)} />
          </Form.Item>
          <Form.Item
            label="Label"
            name="label"
            rules={[{ required: true, message: 'Label is required' }]}
          >
            <Input placeholder="e.g. Pending claims board" />
          </Form.Item>
          <Form.Item label="Group" name="module_group">
            <Input placeholder="e.g. Care, Claims, System" />
          </Form.Item>
          <Form.Item label="Application" name="application">
            <Select options={APPLICATIONS.map((a) => ({ value: a.key, label: a.label }))} />
          </Form.Item>
          <Form.Item
            label="Routes"
            name="routes"
            rules={[{ required: true, type: 'array', min: 1, message: 'Add at least one route' }]}
            extra="Type a path and press Enter. First route is the landing / sidebar link."
          >
            <Select mode="tags" tokenSeparators={[',']} placeholder="/claims" />
          </Form.Item>
          <Form.Item
            label="Permissions (any of)"
            name="any_of"
            extra="Anyone holding one of these permissions can open the module."
          >
            <Select
              mode="tags"
              tokenSeparators={[',']}
              options={permissionOptions}
              placeholder="claims.read"
            />
          </Form.Item>
          <Form.Item label="Description" name="description">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item label="Sort order" name="sort_order">
            <InputNumber min={0} max={10000} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item label="Show in sidebar" name="nav_visible" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item
            label="Allowed as role landing page"
            name="allow_home"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
          <Form.Item label="Active" name="is_active" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
